import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import Badge from '../../../components/common/Badge/Badge'
import {
  getLeaveTypes,
  validateRequestDates,
  checkOverlap,
  getTodayDate,
  formatRequestDate,
  getStatusBadgeVariant
} from '../../../services/timeOffService'
import { calculateDuration } from '../../../services/holidaysService'

/**
 * RequestModal Component
 * Modal form for creating time off requests
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSubmit - Submit form callback
 * @param {object} userData - Current user data (for overlap check)
 * @param {boolean} isLoading - Whether form is submitting
 */
const RequestModal = ({ isOpen, onClose, onSubmit, userData, isLoading = false }) => {
  // Form state
  const [leaveType, setLeaveType] = useState('Annual Leave')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  // Overlap detection state
  const [overlapWarning, setOverlapWarning] = useState(null)
  const [checkingOverlap, setCheckingOverlap] = useState(false)

  // Duration (auto-calculated)
  const duration = startDate && endDate ? calculateDuration(startDate, endDate) : 0

  // Character limit
  const reasonMaxLength = 500

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
  }, [isOpen])

  // Check for overlapping requests (debounced)
  useEffect(() => {
    if (!startDate || !endDate || !userData?.employeeId) {
      setOverlapWarning(null)
      return
    }

    // Validate dates first
    const validation = validateRequestDates(startDate, endDate)
    if (!validation.valid) {
      setOverlapWarning(null)
      return
    }

    // Debounce: wait 500ms after last date change
    const timer = setTimeout(async () => {
      setCheckingOverlap(true)

      const result = await checkOverlap(userData.employeeId, startDate, endDate)

      if (result.success && result.hasOverlap) {
        setOverlapWarning(result.overlapping)
      } else {
        setOverlapWarning(null)
      }

      setCheckingOverlap(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [startDate, endDate, userData])

  const resetForm = () => {
    setLeaveType('Annual Leave')
    setStartDate('')
    setEndDate('')
    setReason('')
    setError('')
    setOverlapWarning(null)
    setCheckingOverlap(false)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate required fields
    if (!leaveType || !startDate || !endDate || !reason.trim()) {
      setError('All fields are required')
      return
    }

    // Validate dates
    const dateValidation = validateRequestDates(startDate, endDate)
    if (!dateValidation.valid) {
      setError(dateValidation.error)
      return
    }

    // Validate reason length
    if (reason.length > reasonMaxLength) {
      setError(`Reason cannot exceed ${reasonMaxLength} characters`)
      return
    }

    // Submit (overlap warning doesn't block submission)
    onSubmit({
      leaveType,
      startDate,
      endDate,
      reason: reason.trim()
    })
  }

  const handleClose = () => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('psyber-modal-overlay')) {
      handleClose()
    }
  }

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value
    setStartDate(newStartDate)

    // Clear error if fixing past date issue
    if (error && error.includes('past')) {
      setError('')
    }
  }

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value
    setEndDate(newEndDate)

    // Clear error if fixing date range issue
    if (error && error.includes('End date')) {
      setError('')
    }
  }

  if (!isOpen) return null

  return (
    <div className="psyber-modal-overlay" onClick={handleBackdropClick}>
      <div className="psyber-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="psyber-modal-header">
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-2xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-calendar-plus"></i>
            Request Time Off
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem',
              borderRadius: 'var(--psyber-radius-sm)',
              transition: 'var(--psyber-transition-fast)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = 'var(--psyber-card-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              border: '2px solid var(--psyber-error)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
              <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Overlap Warning Banner */}
          {overlapWarning && overlapWarning.length > 0 && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-warning-bg)',
              border: '2px solid var(--psyber-warning)',
              borderRadius: 'var(--psyber-radius-md)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <i className="fas fa-exclamation-triangle"
                   style={{ fontSize: '1.5rem', color: 'var(--psyber-warning)', marginTop: '0.125rem' }}></i>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: 'var(--psyber-warning)', display: 'block', marginBottom: '0.5rem' }}>
                    Date Overlap Detected
                  </strong>
                  <p style={{ color: 'var(--psyber-text)', fontSize: 'var(--psyber-font-size-sm)', marginBottom: '0.5rem', margin: 0 }}>
                    This request overlaps with existing time off:
                  </p>
                  <ul style={{ margin: '0.5rem 0 0 1.5rem', fontSize: 'var(--psyber-font-size-sm)', padding: 0 }}>
                    {overlapWarning.map((req, index) => (
                      <li key={index} style={{ marginBottom: '0.25rem' }}>
                        {formatRequestDate(req.startDate, req.endDate)} - {req.leaveType}
                        {' '}
                        <Badge variant={getStatusBadgeVariant(req.status)} style={{ marginLeft: '0.25rem' }}>
                          {req.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                  <p style={{
                    color: 'var(--psyber-text-secondary)',
                    fontSize: 'var(--psyber-font-size-xs)',
                    marginTop: '0.5rem',
                    marginBottom: 0,
                    fontStyle: 'italic'
                  }}>
                    You can still submit this request, but it may be rejected due to scheduling conflicts.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Checking Overlap Indicator */}
          {checkingOverlap && (
            <div style={{
              marginBottom: '1rem',
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <i className="fas fa-spinner fa-spin"></i>
              Checking for conflicts...
            </div>
          )}

          {/* Leave Type Dropdown */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="leaveType"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-tag"></i> Leave Type *
            </label>
            <select
              id="leaveType"
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              disabled={isLoading}
              className="psyber-input"
              style={{
                width: '100%',
                cursor: 'pointer'
              }}
              required
            >
              {getLeaveTypes().map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Date Fields Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* Start Date */}
            <div>
              <label
                htmlFor="startDate"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}
              >
                <i className="fas fa-calendar-day"></i> Start Date *
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={handleStartDateChange}
                min={getTodayDate()}
                disabled={isLoading}
                className="psyber-input"
                style={{ width: '100%' }}
                required
              />
            </div>

            {/* End Date */}
            <div>
              <label
                htmlFor="endDate"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}
              >
                <i className="fas fa-calendar-day"></i> End Date *
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={handleEndDateChange}
                min={startDate || getTodayDate()}
                disabled={isLoading}
                className="psyber-input"
                style={{ width: '100%' }}
                required
              />
            </div>
          </div>

          {/* Duration Display */}
          {duration > 0 && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '0.75rem',
              background: 'var(--psyber-info-bg)',
              border: '1px solid var(--psyber-info)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <i className="fas fa-clock" style={{ color: 'var(--psyber-info)' }}></i>
              <span style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text)' }}>
                <strong>Duration:</strong> {duration} day{duration !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Reason Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="reason"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-comment"></i> Reason *
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for time off request"
              disabled={isLoading}
              maxLength={reasonMaxLength}
              className="psyber-input"
              rows={5}
              style={{
                width: '100%',
                resize: 'vertical',
                minHeight: '100px'
              }}
              required
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: reason.length > reasonMaxLength * 0.9 ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
              textAlign: 'right'
            }}>
              {reason.length}/{reasonMaxLength} characters
            </div>
          </div>

          {/* Help Text */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'var(--psyber-info-bg)',
            border: '1px solid var(--psyber-info)',
            borderRadius: 'var(--psyber-radius-md)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--psyber-info)', marginRight: '0.5rem' }}></i>
            <strong>Note:</strong> Your request will be reviewed by an administrator. You'll be notified once it's approved or rejected.
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid var(--psyber-card-border)'
          }}>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon="fas fa-paper-plane"
              loading={isLoading}
              disabled={isLoading || !leaveType || !startDate || !endDate || !reason.trim()}
            >
              {isLoading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RequestModal
